// Mirrors apps/api/types/alertEnrolment.ts (DTO shapes) + schema enums.
// Shopify is in both lists: Shopify Payments is a real processor, so one Shopify account can be
// named as the processor, the CRM, or both. 'other' marks free text, not a processor.
export const PP_TYPES = ['paypal', 'stripe', 'authorize_net', 'adyen', 'shopify', 'other'] as const;
export type PpType = typeof PP_TYPES[number];

export const CRM_TYPES = ['shopify', 'checkout_champ', 'crm_connector'] as const;
export type CrmType = typeof CRM_TYPES[number];
export const CRM_LABELS: Record<CrmType, string> = { shopify: 'Shopify', checkout_champ: 'Checkout Champ', crm_connector: 'CRM Connector' };

export const ENROLMENT_STATUSES = ['not_submitted', 'submitted', 'enrolled', 'transfer_pending', 'transfer_in_progress'] as const;
export type EnrolmentStatus = typeof ENROLMENT_STATUSES[number];

export const ENROLMENT_NETWORKS = ['ethoca', 'cdrn', 'rdr', 'oice', 'tc', 'discover', 'amex'] as const;
export type EnrolmentNetwork = typeof ENROLMENT_NETWORKS[number];

export interface EnrolmentDescriptor { id: number; descriptor: string; ethocaVerified: boolean; cdrnVerified: boolean; }
export interface EnrolmentCombination { id: number; bin: string; caid: string; mcc: string | null; rdrVerified: boolean; oiceVerified: boolean; tc40Verified: boolean; tc15Verified: boolean; }
export interface Enrolment {
  id: number;
  companyId: number;
  companyName: string | null;
  brandId: number | null;
  brandName: string | null;
  ppType: string;
  ppAccountId: number | null;
  ppAccountName: string | null;
  crmType: string | null;
  crmAccountId: number | null;
  crm: string;
  adyenMerchantAccountCode: string | null;
  checkoutChampGateways: EnrolmentGateway[];
  crmConnectorGateways: EnrolmentCrmGateway[];
  ethocaStatus: EnrolmentStatus;
  cdrnStatus: EnrolmentStatus;
  rdrStatus: EnrolmentStatus;
  oiceStatus: EnrolmentStatus;
  tcStatus: EnrolmentStatus;
  discoverStatus: EnrolmentStatus;
  amexStatus: EnrolmentStatus;
  dashboardAccess: boolean;
  representment: boolean;
  note: string | null;
  descriptors: EnrolmentDescriptor[];
  historicDescriptors: EnrolmentDescriptor[];
  combinations: EnrolmentCombination[];
  createdAt: string;
  updatedAt: string;
}
export interface EnrolmentListResponse { enrolments: Enrolment[]; total: number; }
export interface EnrolmentHistoryEntry {
  id: number; createdAt: string; triggerSource: string; action: string;
  field: string | null; oldValue: string | null; newValue: string | null; actorName: string | null; metadata: unknown | null;
}

export interface EnrolmentDescriptorInput { id?: number; descriptor: string; }
export interface EnrolmentCombinationInput { id?: number; bin: string; caid: string; mcc?: string | null; }
export interface EnrolmentWriteBody {
  ppType: PpType; ppAccountId: number | null; ppAccountName: string | null;
  crmType: CrmType | null; crmAccountId: number | null; crm: string;
  brandId: number | null;
  mappings: EnrolmentMappingInput[];
  descriptors: EnrolmentDescriptorInput[]; historicDescriptors: EnrolmentDescriptorInput[]; combinations: EnrolmentCombinationInput[]; note?: string | null;
}
export interface EnrolmentCreateBody extends EnrolmentWriteBody { companyId: number; }

// The sub-accounts an enrolment settles through, sent as one list whatever the provider.
// `externalRef` is the identity every claim and rate keys on - an Adyen merchant account code, a
// CoC biller id as a string. `externalName` is the provider's label as the picker listed it:
// display only, so an absent one costs a fallback to the ref, never a wrong number.
export type MappingProvider = 'adyen' | 'checkout_champ' | 'crm_connector';
export interface EnrolmentMappingInput { provider: MappingProvider; externalRef: string; externalName: string | null; }

// A CheckoutChamp gateway an enrolment settles through, as the enrolment reads back.
export interface EnrolmentGateway { billerId: number; title: string | null; }

export interface EnrolmentCrmGateway { id: string; name: string | null; }

// One Adyen account is a company spanning many merchant accounts; `id` is the merchant account code.
// `mappedEnrolmentId` names the enrolment already settling through it, so the form can offer a
// merchant as taken instead of letting the save fail on it.
export interface AdyenMerchantOption { id: string; name: string | null; mappedEnrolmentId: number | null; }

// A CheckoutChamp gateway the account offers, same claim contract as AdyenMerchantOption. mid and
// descriptor are what tell two gateways of one processor apart in the picker.
export interface CheckoutChampGatewayOption {
  billerId: number; title: string | null; midNumber: string | null; descriptor: string | null; mappedEnrolmentId: number | null;
}

export interface CrmConnectorGatewayOption { id: string; name: string; mappedEnrolmentId: number | null; }
